import { useState } from 'react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui';
import { usePreferences } from '@/hooks/use-preferences';
import { DATE_FORMATS, formatDate, type Preferences } from '@/lib/preferences';
import { cn } from '@/lib/utils';

export interface PreferencesDialogProps {
  readonly open: boolean;
  readonly today: string;
  readonly onClose: () => void;
}

/**
 * Global display preferences, edited as a draft and applied on Save, so a reader can try
 * a choice against the examples here without the page behind changing under them.
 */
export function PreferencesDialog({ open, today, onClose }: PreferencesDialogProps) {
  return (
    <Modal open={open} title="Preferences" onClose={onClose}>
      <PreferencesForm today={today} onClose={onClose} />
    </Modal>
  );
}

function PreferencesForm({ today, onClose }: { today: string; onClose: () => void }) {
  const { preferences, setPreferences } = usePreferences();
  const [draft, setDraft] = useState<Preferences>(preferences);
  const [refused, setRefused] = useState(false);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (setPreferences(draft)) {
          onClose();
        } else {
          // Applied, but not kept: say so rather than let a reload quietly undo it.
          setRefused(true);
        }
      }}
    >
      <fieldset className="flex flex-col gap-1 px-3 py-3">
        <legend className="mb-1.5 text-xs font-semibold tracking-wide text-ink-2 uppercase">Date format</legend>
        {DATE_FORMATS.map((option) => {
          const selected = draft.dateFormat === option.value;
          return (
            <label
              key={option.value}
              className={cn(
                'flex h-8 cursor-pointer items-center gap-2 rounded-md border px-2.5 text-xs',
                selected ? 'border-accent bg-accent-soft' : 'border-line hover:bg-surface-2',
              )}
            >
              <input
                type="radio"
                name="dateFormat"
                value={option.value}
                checked={selected}
                onChange={() => setDraft({ ...draft, dateFormat: option.value })}
                className="accent-[var(--accent)]"
              />
              <span className="w-24 font-medium">{option.label}</span>
              <span className="ml-auto text-ink-2 tabular">{formatDate(today, option.value)}</span>
            </label>
          );
        })}
        <p className="mt-1 text-[11px] text-muted">Applies to every date on the page. The date pickers in the backtest form follow your browser’s own format.</p>
        {refused && <p className="text-[11px] text-negative">Your browser did not allow saving, so this applies until you reload the page.</p>}
      </fieldset>

      <footer className="flex justify-end gap-2 border-t border-line px-3 py-2">
        <Button onClick={onClose}>{refused ? 'Close' : 'Cancel'}</Button>
        {!refused && (
          <Button type="submit" variant="primary">
            Save
          </Button>
        )}
      </footer>
    </form>
  );
}
